<!-- WHAT THE PHOTOGRAPH IS CARRYING.
  --
  -- This replaces everything below the stage rather than joining it. The other
  -- report-shaped tool here, AsciiPanel, at least produces TEXT; this produces
  -- NO ARTEFACT AT ALL. There is nothing to download, no second version to
  -- choose between, no saving to report and nothing uploaded -- so an options
  -- panel, a versions table and a Download button would all be furniture for
  -- work that never happens.
  --
  -- THE LOCATION IS THE HEADLINE, and the order is the point: a visitor who
  -- reads one line should read the one that can identify their home. Camera
  -- and exposure come last because nobody was ever harmed by their aperture.
  -->
<template>
    <div id="metadata-panel" class="w-full flex flex-col gap-4">

        <div v-if="reading" id="metadata-reading" role="status"
             class="flex items-center gap-3 text-[15px]">
            <span class="loading loading-spinner loading-sm text-brand"></span>
            <span>{{ t('inspect.reading') }}</span>
        </div>

        <!-- NOTHING FOUND IS AN ANSWER, not an empty state. A screenshot or a
          -- picture that has been through a social network carries nothing,
          -- and saying so plainly is the whole service on that file. -->
        <div v-else-if="info && !info.found" id="metadata-none"
             class="rounded-xl border border-white/20 bg-raised p-5 flex flex-col gap-2">
            <h2 class="text-[17px] font-black">{{ t('inspect.noneTitle') }}</h2>
            <p class="text-[15px] text-ink-soft">{{ t('inspect.noneBody') }}</p>
        </div>

        <template v-else-if="info && info.found">
            <!-- The one-line verdict, before any of the detail. -->
            <div id="metadata-verdict"
                 :class="['rounded-xl border p-5 flex flex-col gap-2',
                          info.location ? 'border-error/60 bg-error/10' : 'border-white/20 bg-raised']">
                <h2 class="text-[17px] font-black">
                    {{ info.location ? t('inspect.carriesLocation') : t('inspect.carriesSome') }}
                </h2>
                <p class="text-[15px] text-ink-soft">{{ findingSentence }}</p>
            </div>

            <div class="flex flex-col gap-4">
                <!-- WHERE -->
                <section v-if="info.location" id="metadata-location"
                         class="rounded-xl border border-white/20 bg-raised p-5 flex flex-col gap-3">
                    <h3 class="text-xs font-bold uppercase tracking-[0.09em] text-ink-label">
                        {{ t('inspect.where') }}</h3>
                    <p class="text-[15px] font-bold tabular-nums" data-coord-dms>
                        {{ latText.dms }}, {{ lonText.dms }}</p>
                    <p class="text-[13px] text-ink-mute tabular-nums" data-coord-decimal>
                        {{ latText.decimal }}, {{ lonText.decimal }}</p>
                    <p v-if="altitudeText" class="text-[13px] text-ink-mute">{{ altitudeText }}</p>
                    <p v-if="directionText" class="text-[13px] text-ink-mute">{{ directionText }}</p>
                    <!-- A LINK, NOT A LOOKUP. Nothing reaches a map provider
                      -- until somebody clicks this, which is the whole
                      -- difference between it and geocoding the position on
                      -- load. noreferrer so following it does not also say
                      -- which page it was followed from. -->
                    <a :href="mapHref" target="_blank" rel="noopener noreferrer" data-map-link
                       class="self-start text-[14px] font-bold text-brand underline underline-offset-2">
                        {{ t('inspect.openMap') }}</a>
                </section>

                <!-- WHEN -->
                <section v-if="whenText" id="metadata-when"
                         class="rounded-xl border border-white/20 bg-raised p-5 flex flex-col gap-2">
                    <h3 class="text-xs font-bold uppercase tracking-[0.09em] text-ink-label">
                        {{ t('inspect.when') }}</h3>
                    <p class="text-[15px] font-bold">{{ whenText.date }}</p>
                    <p class="text-[13px] text-ink-mute tabular-nums">{{ whenText.time }}</p>
                </section>

                <!-- WHAT TOOK IT -->
                <section v-if="deviceLine" id="metadata-device"
                         class="rounded-xl border border-white/20 bg-raised p-5 flex flex-col gap-2">
                    <h3 class="text-xs font-bold uppercase tracking-[0.09em] text-ink-label">
                        {{ t('inspect.device') }}</h3>
                    <p class="text-[15px] font-bold">{{ deviceLine }}</p>
                    <p v-if="info.device.lens" class="text-[13px] text-ink-mute">{{ info.device.lens }}</p>
                    <!-- LABELLED, because on every Apple device this tag is
                      -- the OS VERSION -- "14.0", "16.7", "26.6.1" -- and an
                      -- unlabelled "14.0" under the camera reads as a stray
                      -- number. It is worth showing: it says which iOS the
                      -- photograph was written by, which is both fingerprinting
                      -- detail and, if it is old, a security signal. -->
                    <p v-if="info.device.software" class="text-[13px] text-ink-mute">
                        {{ t('inspect.softwareLine', { v: info.device.software }) }}</p>
                </section>

                <!-- HOW -->
                <section v-if="exposureLine" id="metadata-exposure"
                         class="rounded-xl border border-white/20 bg-raised p-5 flex flex-col gap-2">
                    <h3 class="text-xs font-bold uppercase tracking-[0.09em] text-ink-label">
                        {{ t('inspect.settings') }}</h3>
                    <p class="text-[15px] tabular-nums">{{ exposureLine }}</p>
                    <p v-if="info.otherTags" class="text-[13px] text-ink-mute">
                        {{ t('inspect.otherTags', { n: info.otherTags }) }}</p>
                </section>
            </div>
        </template>

        <!-- The way out, and it is the whole reason the pair exists. Shown
          -- whatever was found: somebody who checked and saw nothing may still
          -- want the next photograph cleaned. -->
        <a v-if="info && !reading" href="/remove-exif-data" id="metadata-strip"
           class="self-start text-[15px] font-bold text-brand underline underline-offset-2">
            {{ t('inspect.strip') }} &rarr;</a>
    </div>
</template>

<script setup>
import { computed } from 'vue'
import { useI18n } from 'vue-i18n'
import { readableWhen, readableExposure, readableCoord, readableDirection,
         mapsUrl, findingKeys } from './../lib/exifRead'

const { t } = useI18n()
const props = defineProps({
    info: { type: Object, default: null },
    reading: { type: Boolean, default: false },
})

const latText = computed(() => props.info && props.info.location
    ? readableCoord(props.info.location.lat, true) : null)
const lonText = computed(() => props.info && props.info.location
    ? readableCoord(props.info.location.lon, false) : null)
const mapHref = computed(() => props.info && props.info.location
    ? mapsUrl(props.info.location.lat, props.info.location.lon) : null)

const altitudeText = computed(() => {
    const a = props.info && props.info.location && props.info.location.altitude
    return typeof a === 'number' ? t('inspect.altitude', { n: Math.round(a) }) : null
})
const directionText = computed(() => {
    const d = props.info && props.info.location && props.info.location.direction
    const r = typeof d === 'number' ? readableDirection(d) : null
    return r ? t('inspect.facing', { n: r.degrees, point: t('inspect.points.' + r.point.replace('-', '')) }) : null
})

const whenText = computed(() => props.info && props.info.taken
    ? readableWhen(props.info.taken.when) : null)

const deviceLine = computed(() => {
    const d = (props.info && props.info.device) || {}
    if (!d.make && !d.model) return null
    // "OnePlus OnePlus 13" is what naive concatenation gives, and every
    // phone whose model repeats its maker reads like a stutter.
    if (d.make && d.model && d.model.toLowerCase().startsWith(d.make.toLowerCase())) return d.model
    return [d.make, d.model].filter(Boolean).join(' ')
})

const exposureLine = computed(() => {
    const k = (props.info && props.info.taken) || {}
    const bits = []
    if (k.iso) bits.push('ISO ' + k.iso)
    if (k.fNumber) bits.push('f/' + k.fNumber)
    const sh = readableExposure(k.exposureTime)
    if (sh) bits.push(sh)
    return bits.length ? bits.join(' · ') : null
})

/* The findings as one sentence. Built from the catalogue's own names so the
 * list reads as prose rather than as a comma-separated dump. */
const findingSentence = computed(() => {
    const keys = findingKeys(props.info)
    if (!keys.length) return ''
    const names = keys.map((k) => t('inspect.found.' + k))
    if (names.length === 1) return t('inspect.sentenceOne', { a: names[0] })
    const last = names.pop()
    return t('inspect.sentenceMany', { list: names.join(', '), last })
})
</script>
